import { RiEditLine, RiUserAddLine } from '@remixicon/react';
import { initials, formatMemberStatus } from '../utils/format';

const AVATAR_COLORS = [
  'bg-amber-100 text-amber-900',
  'bg-blue-100 text-blue-900',
  'bg-purple-100 text-purple-900',
  'bg-rose-100 text-rose-900',
  'bg-emerald-100 text-emerald-900',
  'bg-indigo-100 text-indigo-900',
];

const avatarColorFor = (key = '') => {
  let hash = 0;
  for (let i = 0; i < key.length; i += 1) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
};

const formatRole = (role) => {
  if (!role) return '';
  const map = {
    admin: 'Admin',
    ADMIN: 'Admin',
    broker: 'Broker',
    junior_broker: 'Junior Broker',
    finance: 'Finance / Accounting',
  };
  return map[role] || role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const TeamMemberRow = ({ member, isYou, rowIsOwner, viewerIsOwner, onEdit }) => {
  const first = member.first_name || '';
  const last = member.last_name || '';
  const fullName = [first, last].filter(Boolean).join(' ') || member.email || 'Member';
  const colorClass = avatarColorFor(member.id || member.email || fullName);
  // Status chip lets an admin scan at a glance who's Pending Activation /
  // Deactivated without opening the Edit drawer.
  const statusBadge = member.status ? formatMemberStatus(member.status) : null;
  // Edit-button visibility:
  //  - Owner row → only the owner themselves can open it. (Hidden for admins
  //    so they can't change the owner's role.)
  //  - Admin row → only the owner can edit (so the owner can demote admins).
  //  - Anything else → editable as before.
  const rowIsAdmin = member.role === 'admin' || member.role === 'ADMIN';
  const canEditRow = rowIsOwner ? viewerIsOwner : (rowIsAdmin ? viewerIsOwner : true);
  // Show "Owner" instead of the raw role label for the org owner.
  const roleLabel = rowIsOwner ? 'Owner' : formatRole(member.role);
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${colorClass}`}
      >
        {initials(first, last) || (member.email || 'U').charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">
          {fullName}
          {isYou && <span className="font-normal text-gray-500"> (You)</span>}
        </p>
        <p className="text-xs text-gray-500 truncate mt-0.5">{roleLabel}</p>
      </div>
      {statusBadge && (
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${statusBadge.className}`}>
          {statusBadge.label}
        </span>
      )}
      {canEditRow && (
        <button
          type="button"
          onClick={() => onEdit?.(member)}
          className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={`Edit ${fullName}`}
        >
          <RiEditLine className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

const TeamMembersPanel = ({
  members = [],
  currentUserId,
  currentUserprofileId,
  ownedBy,
  onEditMember,
  onAddMember,
}) => {
  const viewerIsOwner = !!ownedBy && currentUserprofileId === ownedBy;
  return (
    <div className="flex flex-col h-[calc(100vh-140px)] bg-white/70 backdrop-blur-sm rounded-[2.5rem] overflow-hidden">
      {/* Scrollable list area — the only part that scrolls. */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        <p className="text-sm text-gray-700 px-1">
          Invite team members to work with and assign member permissions.
        </p>

        <div className="bg-white rounded-2xl divide-y divide-gray-100">
          {members.length === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-500 text-center">
              No team members yet.
            </div>
          ) : (
            members.map((m) => (
              <TeamMemberRow
                key={m.id || m.email}
                member={m}
                isYou={m.id === currentUserId}
                rowIsOwner={!!ownedBy && m.userprofile_id === ownedBy}
                viewerIsOwner={viewerIsOwner}
                onEdit={onEditMember}
              />
            ))
          )}
        </div>
      </div>

      {/* Sticky footer — Add member stays pinned at the bottom of the panel. */}
      <div className="shrink-0 px-4 md:px-6 pb-4 pt-2 bg-white/70 backdrop-blur-sm">
        <button
          type="button"
          onClick={onAddMember}
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-full bg-indigo-50 text-sm font-medium text-primary-700 hover:bg-indigo-100 transition-colors"
        >
          <RiUserAddLine className="w-4 h-4" />
          Add member
        </button>
      </div>
    </div>
  );
};

export default TeamMembersPanel;
